// #589 — the one confirm of the app, in place of the native confirm() and
// prompt(). A native dialog cannot show the shared modal parts, a phone
// browser can block it, and an e2e spec must answer it outside the page.
// This modal uses ModalBackdrop + ModalHeader + ModalFooter (#552): the close
// X, Esc and the focus trap. It opens on top of another modal too (the
// referee modal): only the top modal answers Esc.
import { useEffect, useState } from 'react';
import ModalBackdrop from './ModalBackdrop';
import ModalHeader from './ModalHeader';
import ModalFooter from './ModalFooter';
import Field from './Field';

/** The pause of the second question of a hard delete, in ms (#641, #661). */
export const SECOND_QUESTION_ARM_MS = 600;

/** The pause of each question of useConfirm(), in ms (#665 review). A fast
 *  double click on the button that asks cannot land on the confirm button. */
export const QUESTION_ARM_MS = 300;

interface ConfirmModalProps {
  /** The title, in Title Case, and the name of the dialog. */
  title: string;
  /** The question. */
  message?: React.ReactNode;
  /** The label of the confirm button: the action, for example "Leave". */
  confirmLabel: string;
  /** True while the write runs: the button shows `t.common.saving` and is off. */
  busy?: boolean;
  /** The confirm button stays off for this many ms after the modal opens
   *  (default 0). #641, #661 (bug 81): the second question of a hard delete
   *  arms after a short pause, so a double-click or a held Enter on the first
   *  question cannot answer the second one. The caller gives the second
   *  question its own `key`, so that the pause starts again. Use
   *  `SECOND_QUESTION_ARM_MS`. useConfirm() gives each question at least
   *  `QUESTION_ARM_MS` (#665 review). */
  armMs?: number;
  /** The variant with a text field, in place of prompt(). The confirm button
   *  is off while the text is empty. */
  input?: { label: string; value: string; onChange: (value: string) => void };
  /** The label of the cancel button (#665). Default `t.common.cancel`. */
  cancelLabel?: string;
  /** #665 — the write removes or ends a record. The form carries
   *  `data-destructive`, the confirm button is red (`btn-danger`), and Cancel
   *  gets the first focus, so Enter answers no. */
  destructive?: boolean;
  /** The first focus goes to Cancel, so Enter answers no. The button keeps its look
   *  (not red). For a question where "no" is the safe answer, but the action is not destructive. */
  focusCancel?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  /** The data-testid of the confirm button. The form has the data-testid
   *  `confirm-modal` (#665: `answerConfirm()` of the e2e helpers finds it),
   *  and the Cancel button has `confirm-modal-cancel`. */
  testId?: string;
}

export default function ConfirmModal({
  title, message, confirmLabel, busy = false, armMs = 0, input, cancelLabel, destructive = false, focusCancel = false, onConfirm, onCancel, testId = 'confirm-modal-ok',
}: ConfirmModalProps) {
  const empty = !!input && !input.value.trim();
  // The pause belongs to ONE armMs value: the state holds the value whose timer ended, and `off` is
  // derived from it. The first render after a change of armMs is already off, so no click lands
  // between the change and the effect, and a caller with no `key` gets the pause again.
  const [armedFor, setArmedFor] = useState<number | null>(null);
  useEffect(() => {
    if (armMs <= 0) return;
    const timer = setTimeout(() => setArmedFor(armMs), armMs);
    return () => { clearTimeout(timer); setArmedFor(null); };
  }, [armMs]);
  const off = armMs > 0 && armedFor !== armMs;
  // #665 review — the first focus. A plain question focuses its confirm button: Enter answers yes,
  // as the native confirm() did. A destructive question focuses Cancel: Enter answers no. A text
  // question focuses its field. During the pause the dialog holds the focus (useModalA11y).
  // A question in the pause of a second question also focuses Cancel: a held Enter on the first
  // question must not answer the second one when the pause ends (#661, bug 81).
  const focusFirst = input ? undefined : destructive || focusCancel || armMs >= SECOND_QUESTION_ARM_MS ? 'cancel' : 'save';
  return (
    <ModalBackdrop onClose={onCancel} maxWidth="sm" ariaLabel={title}>
      {/* A form: Enter in the text field confirms, as it did in prompt(). */}
      <form data-testid="confirm-modal" data-destructive={destructive || undefined}
        // A held Enter (key repeat) answers nothing: a person who holds Enter on the trigger
        // must not answer a plain question by repeat. The first press answers.
        onKeyDown={e => { if (e.key === 'Enter' && e.repeat) e.preventDefault(); }}
        onSubmit={e => { e.preventDefault(); if (!busy && !empty && !off) onConfirm(); }}>
        <ModalHeader title={title} />
        {message && <p className="text-sm text-gray-600 whitespace-pre-line">{message}</p>}
        {input && (
          <Field label={input.label} className={message ? 'mt-3' : undefined}>
            <input
              id="confirm-modal-input"
              data-testid="confirm-modal-input"
              data-autofocus
              className="input"
              value={input.value}
              maxLength={500}
              onChange={e => input.onChange(e.target.value)}
            />
          </Field>
        )}
        <ModalFooter
          saveAsSubmit
          busy={busy}
          saveLabel={confirmLabel}
          saveDisabled={empty || off}
          saveTestId={testId}
          saveDanger={destructive}
          focusFirst={focusFirst}
          onCancel={onCancel}
          cancelLabel={cancelLabel}
          cancelTestId="confirm-modal-cancel"
        />
      </form>
    </ModalBackdrop>
  );
}
